import type { AnalyticsPlugin } from "analytics";
import mixpanel from "mixpanel-browser";
import type { Config } from "mixpanel-browser";

interface MixpanelPluginOptions {
  token: string;
  instanceName?: string;
  options?: Partial<Config>;
}

interface MixpanelPluginMethods {
  optOut: (options?: {
    cookie_expiration?: number;
    secure_cookie?: boolean;
    cross_subdomain_cookie?: boolean;
  }) => void;
  optIn: (options?: {
    track_event_name?: string;
    track_properties?: Record<string, unknown>;
  }) => void;
  hasOptedOut: () => boolean;
  hasOptedIn: () => boolean;
  clearOptInOut: (options?: {
    cookie_expiration?: number;
    secure_cookie?: boolean;
    cross_subdomain_cookie?: boolean;
  }) => void;
  setGroup: (
    groupKey: string,
    groupId: string | string[] | number | number[]
  ) => void;
  addGroup: (groupKey: string, groupId: string | number) => void;
  removeGroup: (
    groupKey: string,
    groupId: string | string[] | number | number[]
  ) => void;
  getGroup: (groupKey: string, groupId: string | number) => unknown;
  setSuperProperties: (
    properties: Record<string, unknown>,
    options?: { persistent?: boolean }
  ) => void;
  setSuperPropertiesOnce: (
    properties: Record<string, unknown>,
    defaultValue?: unknown
  ) => void;
  unregisterSuperProperty: (propertyName: string) => void;
  getSuperProperty: (propertyName: string) => unknown;
}

function formatMixpanelUserProperties(
  traits: Record<string, unknown>
): Record<string, unknown> {
  const formatted: Record<string, unknown> = {};

  // List of officially reserved Mixpanel user properties that should have $ prefix
  // Based on: https://docs.mixpanel.com/docs/data-structure/user-profiles
  const specialProperties = [
    "email",
    "name",
    "first_name",
    "last_name",
    "phone",
  ];

  Object.entries(traits).forEach(([key, value]) => {
    if (specialProperties.includes(key)) {
      formatted[`$${key}`] = value;
    } else if (key.startsWith("$")) {
      formatted[key] = value;
    } else {
      formatted[key] = value;
    }
  });

  return formatted;
}

function mixpanelPlugin(config: MixpanelPluginOptions): AnalyticsPlugin {
  const { token, instanceName, options = {} } = config;

  let isLoaded = false;
  // Initialize to default instance; will be updated if using named instance
  let mpInstance: typeof mixpanel = mixpanel;

  return {
    name: "mixpanel",

    initialize: ({
      config: analyticsConfig,
    }: {
      config?: Record<string, unknown>;
    }) => {
      if (!token) {
        throw new Error("Mixpanel token is required");
      }

      const mixpanelOptions: Partial<Config> = {
        debug: Boolean(analyticsConfig?.debug),
        autocapture: options.autocapture,
        ...options,
        loaded: (mixpanelInstance) => {
          isLoaded = true;
          // Call user's loaded callback if provided
          if (options.loaded) {
            options.loaded(mixpanelInstance);
          }
        },
      };

      if (instanceName) {
        // Named instance: init returns the instance
        mpInstance = mixpanel.init(
          token,
          mixpanelOptions,
          instanceName
        ) as typeof mixpanel;
      } else {
        // Default instance: init returns undefined, use global mixpanel
        mixpanel.init(token, mixpanelOptions);
        mpInstance = mixpanel;
      }
    },

    loaded: () => {
      return isLoaded;
    },

    track: ({
      payload,
    }: {
      payload: { event: string; properties?: Record<string, unknown> };
    }) => {
      const { event, properties = {} } = payload;

      mpInstance.track(event, properties);
    },

    identify: ({
      payload,
    }: {
      payload: { userId: string; traits?: Record<string, unknown> };
    }) => {
      const { userId, traits = {} } = payload;

      mpInstance.identify(userId);

      // Apply user traits mapping (adds $ prefix for Mixpanel properties)
      const formattedTraits = formatMixpanelUserProperties(traits);

      if (Object.keys(formattedTraits).length > 0) {
        mpInstance.people.set(formattedTraits);
      }
    },

    reset: () => {
      mpInstance.reset();
    },

    methods: {
      /**
       * Set group membership for the current user
       * @example analytics.plugins.mixpanel.setGroup('company', 'acme-corp')
       * https://docs.mixpanel.com/docs/data-structure/group-analytics
       */
      setGroup: (groupKey, groupId) => {
        mpInstance.set_group(groupKey, groupId);
      },

      /**
       * Add user to a group (for multiple group memberships)
       * @example analytics.plugins.mixpanel.addGroup('department', 'engineering')
       */
      addGroup: (groupKey, groupId) => {
        mpInstance.add_group(groupKey, String(groupId));
      },

      /**
       * Remove user from a group
       * @example analytics.plugins.mixpanel.removeGroup('department', 'engineering')
       */
      removeGroup: (groupKey, groupId) => {
        mpInstance.remove_group(groupKey, groupId);
      },

      /**
       * Get group object for setting group properties
       * @example
       * const group = analytics.plugins.mixpanel.getGroup('company', 'acme-corp');
       * group.set({ plan: 'enterprise', seats: 100 });
       */
      getGroup: (groupKey, groupId) => {
        return mpInstance.get_group(groupKey, String(groupId));
      },

      /**
       * Set super properties that persist across events
       * @example analytics.plugins.mixpanel.setSuperProperties({ plan: 'premium', region: 'US' })
       * https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#setting-super-properties
       */
      setSuperProperties: (properties, options) => {
        mpInstance.register(properties, options);
      },

      /**
       * Set super properties only if they don't already exist
       * @example analytics.plugins.mixpanel.setSuperPropertiesOnce({ firstLoginDate: '2024-01-01' })
       */
      setSuperPropertiesOnce: (properties, defaultValue) => {
        mpInstance.register_once(properties, defaultValue);
      },

      /**
       * Remove a super property
       * @example analytics.plugins.mixpanel.unregisterSuperProperty('outdatedPlan')
       */
      unregisterSuperProperty: (propertyName) => {
        mpInstance.unregister(propertyName);
      },

      /**
       * Get the value of a super property
       * @example const plan = analytics.plugins.mixpanel.getSuperProperty('plan')
       */
      getSuperProperty: (propertyName): unknown => {
        return mpInstance.get_property(propertyName);
      },

      /**
       * Opt out of all tracking
       * @example analytics.plugins.mixpanel.optOut()
       * https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#opt-out-of-tracking
       */
      optOut: (options) => {
        mpInstance.opt_out_tracking(options);
      },

      /**
       * Opt in to tracking (re-enable after opt out)
       * @example analytics.plugins.mixpanel.optIn()
       */
      optIn: (options) => {
        mpInstance.opt_in_tracking(options);
      },

      /**
       * Check if user has opted out
       * @example if (analytics.plugins.mixpanel.hasOptedOut()) { showConsentBanner(); }
       */
      hasOptedOut: (): boolean => {
        return mpInstance.has_opted_out_tracking();
      },

      /**
       * Check if user has opted in
       * @example if (!analytics.plugins.mixpanel.hasOptedIn()) { requestConsent(); }
       */
      hasOptedIn: (): boolean => {
        return mpInstance.has_opted_in_tracking();
      },

      /**
       * Clear opt in/out status
       * @example analytics.plugins.mixpanel.clearOptInOut()
       */
      clearOptInOut: (options) => {
        mpInstance.clear_opt_in_out_tracking(options);
      },
    } as MixpanelPluginMethods,
  };
}

export { mixpanelPlugin };
export type { MixpanelPluginOptions, MixpanelPluginMethods };
